import {
  User,
  Mail,
  Shield,
  Clock,
  Activity,
  Calendar,
  AlertTriangle,
  Server,
  Building,
  Copy,
  CheckCircle,
  XCircle,
  Briefcase,
  Phone,
  MapPin,
  KeyRound,
} from "lucide-react";
import { Badge } from "../../../ui/common-ui/badge";
import { Separator } from "../../../ui/common-ui/separator";
import { Button } from "../../../ui/common-ui/button";
import { GraphNode } from "../types/graph";

interface UserInspectorProps {
  node: GraphNode;
  hideIdentityHeader?: boolean;
}

// Format date to readable format
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get activity level color
const getActivityColor = (activity: number) => {
  if (activity >= 80) return "text-destructive";
  if (activity >= 50) return "text-primary";
  if (activity >= 20) return "text-muted-foreground";
  return "text-muted-foreground/50";
};

export function UserInspector({ node, hideIdentityHeader = false }: UserInspectorProps) {
  const props = node.props || {};

  // Determine cloud type
  const isAzure = props.cloudType === 'AZURE';
  const isAws = props.cloudType === 'AWS';

  // Common fields
  const email = props.email || props.userPrincipalName || "";
  const userName = props.userName || props.displayName || props.username || node.label;
  const roles = props.roles || [];
  const roleNames: Record<string, string> = props.roleNames || {};
  const syncedAt = props.syncedAt;
  const projectId = props.projectId;
  const tenantName = props.tenantName;
  const lastActivity = props.lastActivity;
  const isDormant = props.isDormant;
  const activity = props.activity || 0;
  const lastActivityMessage = props.lastActivityMessage || "";
  const roleCount = props.roleCount || 0;
  const resourceCount = props.resourceCount || 0;
  const alertCount = props.alertCount || 0;

  // Azure-specific fields
  const userPrincipalName = props.userPrincipalName;
  const jobTitle = props.jobTitle;
  const department = props.department;
  const officeLocation = props.officeLocation;
  const mobilePhone = props.mobilePhone;
  const isSSOEnabled = props.isSSOEnabled;
  const createdDateTime = props.createdDateTime;

  // AWS-specific fields
  const arn = props.arn;
  const userId = props.userId;
  const createDate = props.createDate;
  const path = props.path;
  const accountId = props.accountId;

  return (
    <div className="space-y-4">
      {!hideIdentityHeader && (
        <>
          {/* User Header */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-foreground leading-tight">
                  {userName}
                </h3>
                {isDormant !== undefined && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] mt-1 ${isDormant
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "border-primary/30 bg-primary/10 text-primary"
                      }`}
                  >
                    {isDormant ? (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Dormant
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </>
                    )}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <Separator className="bg-border/50" />

          {/* Stats Badges */}
          <div className="flex flex-wrap gap-2">
            {alertCount > 0 && (
              <Badge variant="outline" className="text-xs border-destructive/40 bg-destructive/10 text-destructive">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {alertCount} Alert{alertCount !== 1 ? "s" : ""}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              <Shield className="w-3 h-3 mr-1" />
              {roleCount} Role{roleCount !== 1 ? "s" : ""}
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Server className="w-3 h-3 mr-1" />
              {resourceCount} Resource{resourceCount !== 1 ? "s" : ""}
            </Badge>
          </div>

          <Separator className="bg-border/50" />
        </>
      )}

      {/* Email */}
      {email && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Email
          </p>
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium text-foreground break-all flex-1">
              {email}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-muted/50 shrink-0"
              onClick={() => navigator.clipboard.writeText(email)}
              aria-label="Copy email"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Activity */}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          Activity
        </p>
        <div className="flex items-center gap-2">
          <Activity className={`w-3.5 h-3.5 ${getActivityColor(activity)}`} />
          <span className={`text-xs font-semibold ${getActivityColor(activity)}`}>
            {activity}%
          </span>
          {lastActivityMessage && (
            <span className="text-xs text-muted-foreground">
              ({lastActivityMessage})
            </span>
          )}
        </div>
      </div>

      {/* Last Activity */}
      {lastActivity && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Last Activity
          </p>
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(lastActivity)}
            </span>
          </div>
        </div>
      )}

      {/* Project (GCP) or show only for non-Azure/non-AWS */}
      {projectId && !isAzure && !isAws && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Project
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
              {projectId}
            </span>
          </div>
        </div>
      )}

      {/* AWS-specific fields */}
      {isAws && arn && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            ARN
          </p>
          <div className="flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
              {arn}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-muted/50 shrink-0"
              onClick={() => navigator.clipboard.writeText(arn)}
              aria-label="Copy ARN"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {isAws && userId && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            User ID
          </p>
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
              {userId}
            </span>
          </div>
        </div>
      )}

      {isAws && accountId && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Account ID
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
              {accountId}
            </span>
          </div>
        </div>
      )}

      {isAws && path && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Path
          </p>
          <div className="flex items-center gap-2">
            <Server className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {path}
            </span>
          </div>
        </div>
      )}

      {isAws && createDate && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Created
          </p>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(createDate)}
            </span>
          </div>
        </div>
      )}

      {/* Azure-specific fields */}
      {isAzure && userPrincipalName && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            User Principal Name
          </p>
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium text-foreground break-all flex-1">
              {userPrincipalName}
            </span>
          </div>
        </div>
      )}

      {isAzure && jobTitle && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Job Title
          </p>
          <div className="flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {jobTitle}
            </span>
          </div>
        </div>
      )}

      {isAzure && department && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Department
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {department}
            </span>
          </div>
        </div>
      )}

      {isAzure && officeLocation && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Office Location
          </p>
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {officeLocation}
            </span>
          </div>
        </div>
      )}

      {isAzure && mobilePhone && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Mobile Phone
          </p>
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {mobilePhone}
            </span>
          </div>
        </div>
      )}

      {isAzure && isSSOEnabled !== undefined && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            SSO Status
          </p>
          <div className="flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
            <Badge
              variant="outline"
              className={`text-[10px] ${isSSOEnabled
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-muted-foreground/30 bg-muted/10 text-muted-foreground"
                }`}
            >
              {isSSOEnabled ? "SSO Enabled" : "SSO Disabled"}
            </Badge>
          </div>
        </div>
      )}

      {isAzure && createdDateTime && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Created
          </p>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(createdDateTime)}
            </span>
          </div>
        </div>
      )}

      {/* Tenant */}
      {tenantName && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Tenant
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {tenantName}
            </span>
          </div>
        </div>
      )}

      {/* Synced At */}
      {syncedAt && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Last Synced
          </p>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(syncedAt)}
            </span>
          </div>
        </div>
      )}

      <Separator className="bg-border/50" />

      {/* Roles Preview */}
      {roles.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Assigned Roles
          </p>
          <div className="flex flex-wrap gap-1.5">
            {roles.slice(0, 5).map((roleId: string, idx: number) => {
              // Use roleNames map, fallback to formatted role ID
              const displayName = roleNames[roleId] || (() => {
                const suffix = roleId.split('/').pop() || roleId;
                return suffix.charAt(0).toUpperCase() + suffix.slice(1).replace(/([A-Z])/g, ' $1').trim();
              })();
              return (
                <Badge
                  key={idx}
                  variant="outline"
                  className="text-[10px] px-2 py-0.5 border-border/50 bg-muted/30"
                >
                  {displayName}
                </Badge>
              );
            })}
            {roles.length > 5 && (
              <Badge
                variant="outline"
                className="text-[10px] px-2 py-0.5 border-border/50 bg-muted/30"
              >
                +{roles.length - 5} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
